import React from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'

// Antd
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FlagFilled } from '@ant-design/icons'
import { getPersonalFilter } from '../../store/personalFilterSlice'
import { getPersonalDateFrom, getPersonalDateTo } from '../../store/personalDateSlice'
import { utförareColumn } from './utforareColumn'
import { hanteringColumn } from './hanteringColumn'
import TruncatedText from '../truncatedText/TruncatedText'

const UTFORARE_MAX_LENGTH = 20

const formatDateShort = (date: Date | string) => {
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const formatDateMonth = (date: Date | string) => {
  return moment(date).format('MMM-YY')
}

export const getColor = (risk: number) => {
  switch (risk) {
    case 1:
      return 'yellow'
    case 2:
      return 'orange'
    case 3:
      return 'red'
    case 4:
      return 'black'
    default:
      return 'green'
  }
}

export interface DataType {
  key: React.Key
  flagga: number
  flaggnamn: string
  type?: number
  nivå: number
  namn: string
  medarbetare_id?: number
  personnummer?: string
  från: Date
  till: Date
  created: Date
  utförare: {
    utförare_id: number
    utförare_namn: string
  }[]
}

export const CreateFilterConfig = (data: DataType[]) => {
  const filterState = useSelector(getPersonalFilter)
  // Arv (#28): medarbetar-listans Skapad-fönster bärs med till individsidan via location.state.
  const dateFrom = useSelector(getPersonalDateFrom)
  const dateTo = useSelector(getPersonalDateTo)
  // Återställ sorteringen från Redux: matcha sparad columnKey mot kolumnens key.
  const sortOrderFor = (key: string) => (filterState?.sorter?.columnKey === key && filterState?.sorter?.order ? filterState.sorter.order : null)
  const columns: ColumnsType<DataType> = [
    {
      title: 'Namn',
      dataIndex: 'name',
      key: 'name',
      width: 190,
      fixed: 'left',
      sortOrder: sortOrderFor('name'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Personalens namn' },
      render: (_, coworker) => (
        <TruncatedText
          text={coworker.namn}
          to={{
            pathname: `/app/medarbetare/${coworker.medarbetare_id}`,
            state: { from: dateFrom, to: dateTo },
          }}
        />
      ),
      sorter: (a, b) => a.namn.localeCompare(b.namn),
    },
    // path === 'brukare' ?    {
    //   title: 'Personnummer',
    //   dataIndex: 'personnummer',
    //   key: 'personnummer',
    //   showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Sortera på personnummer' },
    //   filters: Array.from(
    //     new Set(
    //       data.map((d) => {
    //         return d.personnummer
    //       })
    //     )
    //   )
    //     .map((name) => {
    //       return {
    //         value: name ? name : '',
    //         text: name,
    //       }
    //     })
    //     .sort((a, b) => a.value.localeCompare(b.value)),
    //   render: (_, coworker) =>
    //     coworker.personnummer && <>{coworker.personnummer}</>,
    //   sorter: (a, b) => a.personnummer!.localeCompare(b.personnummer!),
    //   onFilter: (value, record) => record.personnummer!.includes(value + ''),
    //   filterSearch: true,
    // }: {},
    {
      title: 'Flagga',
      dataIndex: 'deviation',
      key: 'deviation',
      width: 190,
      sortOrder: sortOrderFor('deviation'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Typ av flagga' },
      sorter: (a, b) => a.flaggnamn.localeCompare(b.flaggnamn),
      render: (_, obj) => (
        <TruncatedText
          text={obj.flaggnamn}
          to={{
            pathname: `/app/avvikelse/flag=${obj.flagga}&id=${obj.key}`,
            state: { flagname: obj.flaggnamn, riskniva: obj.nivå },
          }}
          linkId="flagLink"
        />
      ),
    },
    {
      title: 'Risknivå',
      dataIndex: 'risk',
      key: 'risk',
      width: 115,
      sortOrder: sortOrderFor('risk'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Flaggans risknivå, dvs. dess allvarlighetsgrad' },
      sorter: (a, b) => a.nivå - b.nivå,
      render: (_, obj) => (
        <>
          {obj.nivå} <FlagFilled style={{ color: getColor(obj.nivå) }} />
        </>
      ),
    },
    utförareColumn(data, {
      maxLength: UTFORARE_MAX_LENGTH,
      width: 190,
      sortOrder: sortOrderFor('utförare'),
      filterable: false,
      link: { from: dateFrom ?? undefined, to: dateTo ?? undefined, deviation: filterState?.filter?.deviation },
    }),
    {
      title: 'Skapad',
      dataIndex: 'created',
      key: 'created',
      width: 95,
      sortOrder: sortOrderFor('created'),
      sorter: (a, b) => {
        return new Date(a.created).getTime() - new Date(b.created).getTime()
      },
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Datum för den körning då flaggan genererades' },
      render: (_, obj) => <>{formatDateMonth(obj.created)}</>,
    },
    {
      title: 'Period',
      dataIndex: 'periodStart',
      key: 'periodStart',
      width: 200,
      sortOrder: sortOrderFor('periodStart'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Tidsperiod som flaggan baserades på' },
      sorter: (a, b) => {
        const from = new Date(a.från)
        const to = new Date(b.från)
        return from.getTime() - to.getTime()
      },
      render: (_, obj) => <>{formatDateShort(obj.från)} - {formatDateShort(obj.till)}</>,
    },
    // Missades i PR 6, som bara wirade Brukare-listan. Datan har funnits hela tiden —
    // medarbetareApi skickar `hantering` — men ingen kolumn renderade den.
    hanteringColumn({ sortOrder: sortOrderFor('hantering') }),
    Table.SELECTION_COLUMN,
  ]

  return columns
}
